import { Copy, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface BulkOperationProgressModalProps {
  open: boolean;
  onClose: () => void;
  operation: 'delete' | 'retry' | 'shelve';
  progress: {
    status: 'processing' | 'completed' | 'error';
  };
  result?: {
    success: string[];
    failed: { id: string; error: string }[];
  };
  selectedCount: number;
}

export const BulkOperationProgressModal: React.FC<
  BulkOperationProgressModalProps
> = ({ open, onClose, operation, progress, result, selectedCount }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const isProcessing = progress.status === 'processing';
  const isCompleted =
    progress.status === 'completed' || progress.status === 'error';

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const successCount = result?.success?.length || 0;
  const failedCount = result?.failed?.length || 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        // Prevent closing during processing
        if (!isOpen && !isProcessing) {
          handleClose();
        }
      }}
    >
      <DialogContent
        showCloseButton={!isProcessing}
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          // Prevent closing by clicking outside during processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing with Escape during processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {isProcessing
              ? operation === 'delete'
                ? 'Vorgänge werden gelöscht...'
                : operation === 'shelve'
                  ? 'Vorgänge werden zurückgestellt...'
                  : 'Vorgänge werden zur erneuten Verarbeitung gesendet...'
              : result?.failed?.length > 0
                ? operation === 'delete'
                  ? 'Beim Löschen der Vorgänge sind Fehler aufgetreten'
                  : operation === 'shelve'
                    ? 'Beim Zurückstellen der Vorgänge sind Fehler aufgetreten'
                    : 'Beim Starten der erneuten Verarbeitung sind Fehler aufgetreten'
                : operation === 'delete'
                  ? 'Löschvorgang abgeschlossen'
                  : operation === 'shelve'
                    ? 'Zurückstellen abgeschlossen'
                    : progress.status === 'error'
                      ? 'Start der erneuten Verarbeitung nicht bestätigt'
                      : 'Erneute Verarbeitung angestoßen'}
          </DialogTitle>
          <DialogDescription>
            {isProcessing
              ? operation === 'delete'
                ? `${selectedCount} ${selectedCount === 1 ? 'Vorgang' : 'Vorgänge'} ${selectedCount === 1 ? 'wird' : 'werden'} gelöscht.`
                : operation === 'shelve'
                  ? `${selectedCount} ${selectedCount === 1 ? 'Vorgang' : 'Vorgänge'} ${selectedCount === 1 ? 'wird' : 'werden'} zurückgestellt.`
                  : `${selectedCount} ${selectedCount === 1 ? 'Vorgang' : 'Vorgänge'} ${selectedCount === 1 ? 'wird' : 'werden'} zur erneuten Verarbeitung gesendet.`
              : result?.failed?.length > 0
                ? operation === 'delete'
                  ? 'Beim Löschen der Vorgänge sind Fehler aufgetreten.'
                  : operation === 'shelve'
                    ? 'Beim Zurückstellen der Vorgänge sind Fehler aufgetreten.'
                    : 'Einige Vorgänge konnten nicht zur erneuten Verarbeitung gestartet werden. Den weiteren Verarbeitungsstand sehen Sie im jeweiligen Vorgang.'
                : operation === 'delete'
                  ? 'Der Löschvorgang wurde abgeschlossen.'
                  : operation === 'shelve'
                    ? 'Das Zurückstellen wurde abgeschlossen.'
                    : progress.status === 'error'
                      ? 'Der Start konnte nicht bestätigt werden. Den aktuellen Verarbeitungsstand sehen Sie im jeweiligen Vorgang.'
                      : 'Die erneute Verarbeitung wurde gestartet. Den weiteren Verarbeitungsstand sehen Sie im jeweiligen Vorgang.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Processing Section */}
          {isProcessing && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Results Section */}
          {isCompleted && result && (
            <div className="space-y-4">
              {/* Success Summary */}
              {successCount > 0 && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-800">
                    {operation === 'delete'
                      ? successCount === 1
                        ? '1 Vorgang wurde erfolgreich gelöscht'
                        : `${successCount} Vorgänge wurden erfolgreich gelöscht`
                      : operation === 'shelve'
                        ? successCount === 1
                          ? '1 Vorgang wurde erfolgreich zurückgestellt'
                          : `${successCount} Vorgänge wurden erfolgreich zurückgestellt`
                        : successCount === 1
                          ? '1 Vorgang wurde erfolgreich zur erneuten Verarbeitung gesendet'
                          : `${successCount} Vorgänge wurden erfolgreich zur erneuten Verarbeitung gesendet`}
                  </span>
                </div>
              )}

              {/* Error Summary */}
              {failedCount > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <span className="text-sm text-red-800 font-medium">
                      {failedCount === 1
                        ? '1 Vorgang ist fehlgeschlagen'
                        : `${failedCount} Vorgänge sind fehlgeschlagen`}
                    </span>
                  </div>

                  {/* Error Details List */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                      <h4 className="text-sm font-semibold text-text-primary">
                        Fehlerdetails
                      </h4>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {result.failed.map((item, index) => (
                        <div
                          key={item.id}
                          className={`px-4 py-3 border-b border-gray-100 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-text-secondary uppercase">
                                  Vorgang ID:
                                </span>
                                <code className="text-xs font-mono text-text-primary bg-gray-100 px-2 py-0.5 rounded">
                                  {item.id}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0"
                                  onClick={() => handleCopyId(item.id)}
                                  title="ID kopieren"
                                >
                                  {copiedId === item.id ? (
                                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                                  ) : (
                                    <Copy className="h-3 w-3 text-text-secondary" />
                                  )}
                                </Button>
                              </div>
                              <div className="text-sm text-red-700">
                                {item.error}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Status (if operation failed completely) */}
          {progress.status === 'error' && !result && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-800">
                {operation === 'delete'
                  ? 'Die Löschung ist fehlgeschlagen. Bitte versuchen Sie es erneut.'
                  : operation === 'shelve'
                    ? 'Das Zurückstellen ist fehlgeschlagen. Bitte versuchen Sie es erneut.'
                    : 'Der Start konnte nicht bestätigt werden. Den aktuellen Verarbeitungsstand sehen Sie im jeweiligen Vorgang.'}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            onClick={handleClose}
            disabled={isProcessing}
            variant={failedCount > 0 ? 'outline' : 'default'}
          >
            {isProcessing
              ? operation === 'delete'
                ? 'Löschung läuft...'
                : operation === 'shelve'
                  ? 'Zurückstellen läuft...'
                  : 'Verarbeitung läuft...'
              : 'Schließen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
